import { Divider, Stack } from '@mui/material'
import { tokens } from 'ComponentLibrary'
import { isGermanLocale } from 'ComponentLibrary/src/common/locale'
import { CityIcon } from '@phosphor-icons/react'
import { Typography } from 'ComponentLibrary/src/piazza'
import { ValuationStructure } from 'ComponentLibrary/src/widgets/AssetInfoBoxes/types'
import InfoValueWithUnit from 'ComponentLibrary/src/widgets/AssetInfoBoxes/InfoValueWithUnit'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { StructureUsageType } from 'ComponentLibrary/src/common/usage-type'
import { useTranslation } from 'react-i18next'

export type MultiStructuresInfoBoxCopy = {
    sectionLabel: string
    title: string
    usageTypeLabel: string
    actualConstructionYearLabel: string
    economicConstructionYearLabel: string
    usageTypeLabels: Record<StructureUsageType, string>
}

type MultiStructuresInfoBoxProps = {
    structures: ValuationStructure[]
    copy: MultiStructuresInfoBoxCopy
    isPending?: boolean
    hideFictionalConstructionYear?: boolean
}

export function MultiStructuresInfoBox({
    structures,
    copy,
    isPending,
    hideFictionalConstructionYear = false,
}: MultiStructuresInfoBoxProps) {
    const { i18n } = useTranslation()
    const shouldShowSectionLabel = isGermanLocale(i18n.resolvedLanguage ?? i18n.language)

    return (
        <Stack
            direction="column"
            padding={tokens.SizingSpacing200}
            gap={tokens.SizingSpacing150}
            border={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}
            borderRadius={tokens.SizingCornerL}
            sx={{ backgroundColor: tokens.ColorBackgroundLight }}
        >
            <CityIcon size={tokens.SizingIconM} color={tokens.ColorIconBoldest} />
            <Stack direction="column">
                {shouldShowSectionLabel && (
                    <Typography variant="headline5" color={tokens.ColorTextSubtle}>
                        {copy.sectionLabel}
                    </Typography>
                )}
                <Headline title={copy.title} variant="headline4" />
                <Stack direction="column" gap={tokens.SizingSpacing150}>
                    {structures.map((structure, index) => {
                        const isLastStructure = index === structures.length - 1
                        return (
                            <Stack key={structure.title} direction="column" gap={tokens.SizingSpacing050}>
                                <Typography variant="textS" color={tokens.ColorTextBold}>
                                    {structure.title}
                                </Typography>
                                <InfoValueWithUnit
                                    value={copy.usageTypeLabels[structure.usage_type]}
                                    label={copy.usageTypeLabel}
                                    isPending={isPending}
                                    variant="spacious"
                                />
                                <InfoValueWithUnit
                                    value={structure.construction_year}
                                    label={copy.actualConstructionYearLabel}
                                    isPending={isPending}
                                    variant="spacious"
                                />
                                {!hideFictionalConstructionYear && (
                                    <InfoValueWithUnit
                                        value={structure.fictional_construction_year}
                                        label={copy.economicConstructionYearLabel}
                                        isPending={isPending}
                                        variant="spacious"
                                    />
                                )}
                                {!isLastStructure && <Divider sx={{ marginTop: tokens.SizingSpacing150 }} />}
                            </Stack>
                        )
                    })}
                </Stack>
            </Stack>
        </Stack>
    )
}
